/**
 * VenuesPage.js - Dedicated page for managing venues (user-scoped)
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import './VenuesPage.css';

function VenuesPage({ onBack }) {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', address: '', fieldCount: 1, notes: '' });
  const [addLoading, setAddLoading] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', address: '', fieldCount: 1, notes: '' });
  const [editLoading, setEditLoading] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchVenues = useCallback(async () => {
    try {
      const result = await api.getVenues();
      setVenues(result.venues);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleCreate = async () => {
    if (!addForm.name.trim()) return;
    setAddLoading(true);
    try {
      const result = await api.createVenue({
        name: addForm.name.trim(),
        address: addForm.address.trim() || null,
        fieldCount: Math.max(1, Math.floor(addForm.fieldCount)) || 1,
        notes: addForm.notes.trim() || null
      });
      setVenues(prev => [...prev, result.venue].sort((a, b) => a.name.localeCompare(b.name)));
      setAddForm({ name: '', address: '', fieldCount: 1, notes: '' });
      setShowAddForm(false);
      showToast('Venue created');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = (venue) => {
    setEditingId(venue.id);
    setEditForm({
      name: venue.name,
      address: venue.address || '',
      fieldCount: venue.fieldCount || 1,
      notes: venue.notes || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) return;
    setEditLoading(true);
    try {
      const result = await api.updateVenue(editingId, {
        name: editForm.name.trim(),
        address: editForm.address.trim() || null,
        fieldCount: Math.max(1, Math.floor(editForm.fieldCount)) || 1,
        notes: editForm.notes.trim() || null
      });
      setVenues(prev =>
        prev.map(v => v.id === editingId ? { ...v, ...result.venue } : v)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingId(null);
      showToast('Venue updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (venue) => {
    if (!window.confirm(`Delete venue "${venue.name}"?`)) return;
    try {
      await api.deleteVenue(venue.id);
      setVenues(prev => prev.filter(v => v.id !== venue.id));
      showToast('Venue deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const filtered = venues.filter(v => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      v.name.toLowerCase().includes(q) ||
      (v.address && v.address.toLowerCase().includes(q))
    );
  });

  if (loading) {
    return (
      <div className="venues-container">
        <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading venues...</p>
      </div>
    );
  }

  return (
    <div className="venues-container">
      <button className="venues-back" onClick={onBack}>&larr; Dashboard</button>

      <div className="venues-header">
        <h2>
          Venues
          <span className="venues-count">{venues.length}</span>
        </h2>
        <button
          className="venues-btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Venue'}
        </button>
      </div>

      {showAddForm && (
        <div className="venues-add-form">
          <h3>New Venue</h3>
          <div className="venues-form-grid">
            <input
              placeholder="Name *"
              value={addForm.name}
              onChange={e => setAddForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <input
              placeholder="Address"
              value={addForm.address}
              onChange={e => setAddForm(prev => ({ ...prev, address: e.target.value }))}
            />
            <div className="venues-field-count-input">
              <label>Fields/Courts</label>
              <input
                type="number"
                min="1"
                value={addForm.fieldCount}
                onChange={e => setAddForm(prev => ({ ...prev, fieldCount: parseInt(e.target.value) || 1 }))}
              />
            </div>
            <input
              placeholder="Notes (e.g., parking info, indoor/outdoor)"
              value={addForm.notes}
              onChange={e => setAddForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>
          <div className="venues-form-actions">
            <button
              className="venues-btn-primary"
              onClick={handleCreate}
              disabled={!addForm.name.trim() || addLoading}
            >
              {addLoading ? 'Creating...' : 'Create Venue'}
            </button>
          </div>
        </div>
      )}

      {venues.length > 3 && (
        <div className="venues-search">
          <input
            placeholder="Search by name or address..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}>Clear</button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="venues-empty">
          {venues.length === 0 ? (
            <>
              <p><strong>No venues yet</strong></p>
              <p>Add venues here and assign them to games or time slots when creating leagues.</p>
            </>
          ) : (
            <p>No venues matching "{searchQuery}"</p>
          )}
        </div>
      ) : (
        <div className="venues-list">
          {filtered.map(venue => (
            <div key={venue.id} className="venues-card">
              {editingId === venue.id ? (
                /* Edit mode */
                <div>
                  <div className="venues-form-grid">
                    <input
                      placeholder="Name *"
                      value={editForm.name}
                      onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <input
                      placeholder="Address"
                      value={editForm.address}
                      onChange={e => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                    />
                    <div className="venues-field-count-input">
                      <label>Fields/Courts</label>
                      <input
                        type="number"
                        min="1"
                        value={editForm.fieldCount}
                        onChange={e => setEditForm(prev => ({ ...prev, fieldCount: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                    <input
                      placeholder="Notes"
                      value={editForm.notes}
                      onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>
                  <div className="venues-form-actions" style={{ marginTop: '0.5rem' }}>
                    <button
                      className="venues-btn-primary"
                      onClick={handleSaveEdit}
                      disabled={!editForm.name.trim() || editLoading}
                    >
                      {editLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="venues-btn-secondary"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* Display mode */
                <div className="venues-card-header">
                  <div className="venues-card-info">
                    <p className="venues-card-name">
                      {venue.name}
                      {venue.fieldCount > 1 && (
                        <span className="venues-field-badge">{venue.fieldCount} fields</span>
                      )}
                    </p>
                    {venue.address && (
                      <p className="venues-card-detail">{venue.address}</p>
                    )}
                    {venue.notes && (
                      <p className="venues-card-notes">{venue.notes}</p>
                    )}
                    {venue.leagues && venue.leagues.length > 0 && (
                      <div className="venues-card-leagues">
                        <div className="venues-card-leagues-label">Used in</div>
                        {venue.leagues.map(l => (
                          <span key={l.id} className="venues-league-tag">
                            {l.name}
                          </span>
                        ))}
                        <span className="venues-game-count">{venue.gameCount} game{venue.gameCount !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                  <div className="venues-card-actions">
                    <button onClick={() => handleEdit(venue)}>Edit</button>
                    <button className="venues-btn-delete" onClick={() => handleDelete(venue)}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`venues-toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}

export default VenuesPage;
